import React, { useState, useEffect } from 'react';
import {
  Search,
  Package,
  User,
  Calendar,
  Clock,
  Hash,
  Box,
  RefreshCw,
  Activity,
  Archive,
  TrendingUp,
  Plus,
  Minus,
  Edit,
  ExternalLink
} from 'lucide-react';
import { Button, Input, Card, Badge, LoadingSpinner, Notification } from './SharedComponents';
import moment from 'moment-timezone';

const API_URL = import.meta.env.VITE_API_URL;

const InboundHistory = () => {
  // State management
  const [shelfLogs, setShelfLogs] = useState([]);
  const [inboundLogs, setInboundLogs] = useState([]);
  const [filteredShelfLogs, setFilteredShelfLogs] = useState([]);
  const [filteredInboundLogs, setFilteredInboundLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });

  // Show notification helper
  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: 'success' }), 3000);
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  useEffect(() => {
    handleLocalSearch();
  }, [searchTerm, shelfLogs, inboundLogs]);

  const fetchLogs = async () => {
    setIsLoading(true);
    try {
      const [shelfResponse, inboundResponse] = await Promise.all([
        fetch(`${API_URL}/api/shelf_logs`),
        fetch(`${API_URL}/api/inbound_tool_log`)
      ]);

      if (!shelfResponse.ok || !inboundResponse.ok) {
        throw new Error('Failed to fetch logs');
      }

      const shelfData = await shelfResponse.json();
      const inboundData = await inboundResponse.json();

      setShelfLogs(shelfData);
      setInboundLogs(inboundData);
      setFilteredShelfLogs(shelfData);
      setFilteredInboundLogs(inboundData);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching logs:', error);
      showNotification('Failed to load history data', 'error');
      setIsLoading(false);
    }
  };

  const handleLocalSearch = () => {
    if (searchTerm) {
      const lowerCaseSearchTerm = searchTerm.toLowerCase();
      const filteredShelf = shelfLogs.filter(log =>
        log.SKU?.toLowerCase().includes(lowerCaseSearchTerm) ||
        log.EMPLOYEE?.toLowerCase().includes(lowerCaseSearchTerm) ||
        log.FULL_SHELF_CODE?.toLowerCase().includes(lowerCaseSearchTerm)
      );
      const filteredInbound = inboundLogs.filter(log =>
        log.SKU?.toLowerCase().includes(lowerCaseSearchTerm) ||
        log.EMPLOYEE?.toLowerCase().includes(lowerCaseSearchTerm) ||
        log.INBOUND_ID?.toString().includes(lowerCaseSearchTerm)
      );
      setFilteredShelfLogs(filteredShelf);
      setFilteredInboundLogs(filteredInbound);
    } else {
      setFilteredShelfLogs(shelfLogs);
      setFilteredInboundLogs(inboundLogs);
    }
  };

  // Helper function to format the date
  const formatDate = (dateString) => {
    return moment.tz(dateString, 'America/New_York').format('dddd, MMM D, YYYY [at] h:mm A');
  };

  // Get log type styling
  const getLogTypeConfig = (type) => {
    const typeConfigs = {
      'ADD': { variant: 'success', label: 'Added', icon: Plus },
      'UPDATE': { variant: 'info', label: 'Updated', icon: Edit },
      'REMOVE': { variant: 'error', label: 'Removed', icon: Minus },
      'TRANSFER': { variant: 'warning', label: 'Transferred', icon: TrendingUp },
      default: { variant: 'default', label: type || 'Unknown', icon: Activity }
    };
    return typeConfigs[type?.toUpperCase()] || typeConfigs.default;
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Notification */}
      <Notification
        show={notification.show}
        message={notification.message}
        type={notification.type}
        onClose={() => setNotification({ show: false, message: '', type: 'success' })}
      />

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Inbound History</h1>
            <p className="text-gray-600">Track all inventory movements and inbound operations</p>
          </div>
          <Button
            onClick={fetchLogs}
            variant="outline"
            disabled={isLoading}
            className="flex items-center"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Search */}
        <Card className="p-4">
          <Input
            placeholder="Search by SKU, employee, or inbound ID"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon={Search}
            className="text-base"
          />
        </Card>
      </div>

      {/* Loading State */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <LoadingSpinner size="lg" className="mb-4" />
          <div className="ml-4">
            <h3 className="text-lg font-medium text-gray-900">Loading history...</h3>
            <p className="text-gray-500">Please wait while we fetch the latest log data.</p>
          </div>
        </div>
      ) : (
        <>
          {/* Shelf Logs Section */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Shelf Logs</h2>
            {filteredShelfLogs.length > 0 ? (
              <div className="grid grid-cols-1 gap-4">
                {filteredShelfLogs.map((log, index) => {
                  const typeConfig = getLogTypeConfig(log.TYPE);
                  return (
                    <Card key={index} className="p-6 hover:shadow-md transition-shadow">
                      <div className="flex items-start space-x-4">
                        <div className={`p-2 rounded-full ${
                          typeConfig.variant === 'success' ? 'bg-green-100' :
                          typeConfig.variant === 'error' ? 'bg-red-100' :
                          typeConfig.variant === 'warning' ? 'bg-yellow-100' :
                          typeConfig.variant === 'info' ? 'bg-blue-100' : 'bg-gray-100'
                        }`}>
                          <typeConfig.icon className={`w-5 h-5 ${
                            typeConfig.variant === 'success' ? 'text-green-600' :
                            typeConfig.variant === 'error' ? 'text-red-600' :
                            typeConfig.variant === 'warning' ? 'text-yellow-600' :
                            typeConfig.variant === 'info' ? 'text-blue-600' : 'text-gray-600'
                          }`} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center space-x-3">
                              <Badge variant={typeConfig.variant}>
                                {typeConfig.label}
                              </Badge>
                              <span className="text-sm font-medium text-gray-900">Shelf Activity</span>
                            </div>
                            <span className="text-sm text-gray-500">{moment(log.UPDATED).fromNow()}</span>
                          </div>

                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                            <div>
                              <p className="text-xs text-gray-500 mb-1">Employee</p>
                              <div className="flex items-center">
                                <User className="w-4 h-4 mr-1 text-gray-400" />
                                <span className="text-sm font-medium">{log.EMPLOYEE}</span>
                              </div>
                            </div>

                            <div>
                              <p className="text-xs text-gray-500 mb-1">SKU</p>
                              <div className="flex items-center">
                                <Hash className="w-4 h-4 mr-1 text-gray-400" />
                                <code className="text-sm bg-gray-100 px-1 rounded">{log.SKU}</code>
                              </div>
                            </div>

                            <div>
                              <p className="text-xs text-gray-500 mb-1">Quantity</p>
                              <div className="flex items-center">
                                <Package className="w-4 h-4 mr-1 text-gray-400" />
                                <span className="text-sm font-medium">{log.QTY}</span>
                              </div>
                            </div>

                            <div>
                              <p className="text-xs text-gray-500 mb-1">Shelf Code</p>
                              <div className="flex items-center">
                                <Box className="w-4 h-4 mr-1 text-gray-400" />
                                <code className="text-sm bg-blue-100 text-blue-800 px-1 rounded">{log.FULL_SHELF_CODE}</code>
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center text-xs text-gray-500">
                            <Clock className="w-3 h-3 mr-1" />
                            {formatDate(log.UPDATED)}
                          </div>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <Card className="p-6 text-center">
                <p className="text-gray-500">No shelf logs found.</p>
              </Card>
            )}
          </div>

          {/* Inbound Tool Logs Section */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Inbound Tool Logs</h2>
            {filteredInboundLogs.length > 0 ? (
              <div className="grid grid-cols-1 gap-4">
                {filteredInboundLogs.map((log, index) => {
                  const typeConfig = getLogTypeConfig(log.TYPE);
                  return (
                    <Card key={index} className="p-6 hover:shadow-md transition-shadow">
                      <div className="flex items-start space-x-4">
                        <div className={`p-2 rounded-full ${
                          typeConfig.variant === 'success' ? 'bg-green-100' :
                          typeConfig.variant === 'error' ? 'bg-red-100' :
                          typeConfig.variant === 'warning' ? 'bg-yellow-100' :
                          typeConfig.variant === 'info' ? 'bg-blue-100' : 'bg-gray-100'
                        }`}>
                          <typeConfig.icon className={`w-5 h-5 ${
                            typeConfig.variant === 'success' ? 'text-green-600' :
                            typeConfig.variant === 'error' ? 'text-red-600' :
                            typeConfig.variant === 'warning' ? 'text-yellow-600' :
                            typeConfig.variant === 'info' ? 'text-blue-600' : 'text-gray-600'
                          }`} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center space-x-3">
                              <Badge variant={typeConfig.variant}>
                                {typeConfig.label}
                              </Badge>
                              <span className="text-sm font-medium text-gray-900">Inbound Activity</span>
                            </div>
                            <span className="text-sm text-gray-500">{moment(log.UPDATED).fromNow()}</span>
                          </div>

                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                            <div>
                              <p className="text-xs text-gray-500 mb-1">Employee</p>
                              <div className="flex items-center">
                                <User className="w-4 h-4 mr-1 text-gray-400" />
                                <span className="text-sm font-medium">{log.EMPLOYEE}</span>
                              </div>
                            </div>

                            <div>
                              <p className="text-xs text-gray-500 mb-1">Inbound ID</p>
                              <div className="flex items-center">
                                <Package className="w-4 h-4 mr-1 text-gray-400" />
                                <a
                                  href={`https://app.yyzprep.ca/staff/shipments/inbound/${log.INBOUND_ID}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:text-blue-800 hover:underline font-medium flex items-center"
                                >
                                  #{log.INBOUND_ID}
                                  <ExternalLink className="w-3 h-3 ml-1" />
                                </a>
                              </div>
                            </div>

                            <div>
                              <p className="text-xs text-gray-500 mb-1">SKU</p>
                              <div className="flex items-center">
                                <Hash className="w-4 h-4 mr-1 text-gray-400" />
                                <code className="text-sm bg-gray-100 px-1 rounded">{log.SKU}</code>
                              </div>
                            </div>

                            <div>
                              <p className="text-xs text-gray-500 mb-1">Quantity</p>
                              <div className="flex items-center">
                                <Package className="w-4 h-4 mr-1 text-gray-400" />
                                <span className="text-sm font-medium">{log.QTY}</span>
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center text-xs text-gray-500">
                            <Clock className="w-3 h-3 mr-1" />
                            {formatDate(log.UPDATED)}
                          </div>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <Card className="p-6 text-center">
                <p className="text-gray-500">No inbound tool logs found.</p>
              </Card>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default InboundHistory;